import { encerrarSessao } from "./sessao";
import { useCallback, useEffect, useState } from "react";
import { TABS, TIPOS_ANALISE, SUBABAS_SERINGAS, SUBABAS_EQUIP_ENSAIO } from "./config";
import type { SubAbaDef } from "./types";
import { useFaixaDeAbas } from "./faixa-abas";
import { api, setSessionUser, type ResumoEnsaios } from "./api";
import { ICONS, IconDashboard, IconSun, IconMoon, IconLogout, IconHome, IconChevronDown } from "./icons";
import AnimatedBackground from "./components/AnimatedBackground";
import SolicitarAtualizacaoView from "./components/SolicitarAtualizacaoView";
import { AvisoReuniao } from "./components/PedirReuniaoView";
import Login from "./components/Login";
import logoBranca from "./assets/logo-branca.png";
import { applyTheme, getInitialTheme, type Theme } from "./theme";
import {
  SYSTEMS,
  goToSystem,
  checkAcesso,
  readSsoUser,
  clearSsoFromUrl,
} from "./integration";
import Dashboard from "./components/Dashboard";
import CrudView from "./components/CrudView";
import AnalisesView from "./components/AnalisesView";
import ProtocoloView from "./components/ProtocoloView";
import SeringasView from "./components/SeringasView";
import EquipamentosEnsaioView from "./components/EquipamentosEnsaioView";
import PermissoesView from "./components/PermissoesView";
import Historico from "./components/Historico";
import AprovacaoView from "./components/AprovacaoView";
import { AccessChecking, AccessBlocked } from "./components/AccessGate";
import AvisoSistema from "./components/AvisoSistema";
import { PermissoesProvider } from "./permissoes";
import {
  ACESSO_COMPLETO,
  ativarModoLeitura,
  podeVerAba as liberadaNoPortal,
  primeiraAbaLiberada,
  type Visualizacao,
} from "./modo-leitura";

const SESSION_KEY = "sercamp-lab-user";

/** Login inicial: prioriza o SSO vindo do Portal/outros sistemas (URL) e cai na sessão salva. */
function getInitialUser(): string | null {
  const sso = readSsoUser();
  if (sso) {
    localStorage.setItem(SESSION_KEY, sso);
    return sso;
  }
  return localStorage.getItem(SESSION_KEY);
}

/** Estado do portão de acesso do Portal (FASE 2). */
type AccessState =
  | { status: "checking" }
  | { status: "allowed" }
  | { status: "blocked"; message: string };

/** Aba inicial via deep-link do Portal (#tab=chave); cai em "dashboard" se inválida. */
const INITIAL_TAB: string = (() => {
  try {
    const m = window.location.hash.match(/[#&]tab=([^&]+)/);
    const k = m ? decodeURIComponent(m[1]) : "";
    return k && TABS.some((t) => t.key === k) ? k : "dashboard";
  } catch {
    return "dashboard";
  }
})();

/**
 * As abas cujo item do menu abre um SUBMENU de etapas (cadastro → estoque →
 * saída → retorno) — o mesmo desenho nas duas. Um mapa, e não um bloco
 * copiado por aba: a segunda cópia é a que esquece o título ou o chevron.
 * "Análises" fica de fora de propósito: o submenu dela tem contador por ensaio.
 */
const SUBMENUS: Record<string, { titulo: string; subabas: SubAbaDef[] }> = {
  seringas: { titulo: "Controle de Seringas", subabas: SUBABAS_SERINGAS },
  equipamentos_ensaio: { titulo: "Equipamentos de Ensaio", subabas: SUBABAS_EQUIP_ENSAIO },
};

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [user, setUser] = useState<string | null>(getInitialUser);

  useEffect(() => {
    clearSsoFromUrl();
  }, []);
  const [active, setActive] = useState<string>(INITIAL_TAB);
  /*
   * SUBMENU DA ABA "ANÁLISES" — os quatro ensaios descem dentro do próprio item
   * do menu (Físico-Químico, Gases Dissolvidos, PCB, Furanos). Quem manda no
   * ensaio escolhido é daqui: a tela de análises só obedece, e avisa de volta
   * quando a pessoa escolhe por lá (cartão ou "voltar") — assim o item aceso no
   * menu e o que está na tela nunca discordam.
   */
  const [analiseTipo, setAnaliseTipo] = useState<string | null>(null);
  const [analisesAberto, setAnalisesAberto] = useState<boolean>(INITIAL_TAB === "analises");
  /*
   * SUBMENUS DE "CONTROLE DE SERINGAS" E "EQUIPAMENTOS DE ENSAIO" (ver SUBMENUS)
   * — mesmo desenho do de Análises: as quatro etapas (Cadastro, Estoque, Saída,
   * Retorno) descem dentro do próprio item do menu. Quem manda na etapa
   * escolhida é daqui, uma por aba; a tela só obedece e avisa de volta quando a
   * pessoa escolhe por lá (cartão ou "voltar").
   */
  const [subEscolhida, setSubEscolhida] = useState<Record<string, string | null>>({});
  const [subAberto, setSubAberto] = useState<Record<string, boolean>>(() =>
    SUBMENUS[INITIAL_TAB] ? { [INITIAL_TAB]: true } : {},
  );
  const escolherSub = useCallback((aba: string, k: string | null) => {
    setSubEscolhida((m) => ({ ...m, [aba]: k }));
    if (k) setSubAberto((m) => ({ ...m, [aba]: true }));
  }, []);
  /**
   * Quanto serviço em aberto cada ensaio tem — o número que fica no submenu.
   * Sem ele o menu é só uma lista de nomes: a pessoa teria de abrir os quatro
   * para descobrir onde está o trabalho. Falha de rede não quebra nada: o
   * submenu simplesmente aparece sem contador.
   */
  const [resumoEnsaios, setResumoEnsaios] = useState<ResumoEnsaios["ensaios"]>({});
  /** OS esperando entrada no laboratório — o número ao lado de "Protocolo". */
  const [pendentesProtocolo, setPendentesProtocolo] = useState(0);
  /**
   * "Nova amostra" nas Análises abre a criação NO PROTOCOLO (amostra nova entra
   * sempre por lá). `n` muda a cada clique, para o mesmo pedido abrir de novo.
   */
  const [pedidoNovaOs, setPedidoNovaOs] = useState<{ os: string | null; cliente: string | null; n: number } | null>(
    null,
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  // Excluir é exclusivo de administrador (regra do Portal) — o servidor decide e
  // a tela só esconde os botões. Começa em false: no pior caso some um botão,
  // nunca aparece um que o backend recusaria.
  const [podeExcluir, setPodeExcluir] = useState(false);
  /*
   * DIAGNÓSTICO e LIBERAÇÃO não são de todo mundo: quem interpreta/assina e quem
   * solta o resultado para o cliente são poucos, e cadastrar usuário novo NÃO dá
   * esse poder (ver server/permissoes.ts). Começa tudo `false`: no pior caso
   * some um botão — nunca aparece um que o servidor recusaria.
   */
  const [podeDiagnosticar, setPodeDiagnosticar] = useState(false);
  const [podeLiberar, setPodeLiberar] = useState(false);
  /** As abas liberadas para esta pessoa (vazio = todas — ver src/permissoes.tsx). */
  const [abasPermitidas, setAbasPermitidas] = useState<string[]>([]);
  /* O App é quem FORNECE o contexto de permissões, então ele não pode
     consumi-lo (leria o valor de fora, que é vazio): a conta é feita aqui. */
  const podeVerAba = (key: string) => abasPermitidas.length === 0 || abasPermitidas.includes(key);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [access, setAccess] = useState<AccessState>({ status: "checking" });
  // Somente visualização (cadastro do Portal): abas liberadas e nada de gravar.
  const [vis, setVis] = useState<Visualizacao>(ACESSO_COMPLETO);
  /* No celular o menu vira uma faixa que rola de lado: isto mantem a aba
     aberta a vista (ver src/faixa-abas.tsx). */
  const navRef = useFaixaDeAbas(active, access.status === "allowed");

  // PORTÃO DE ACESSO (FASE 2): toda vez que há um usuário atual (login novo OU
  // sessão restaurada ao recarregar), o Portal valida o acesso a ESTE sistema
  // ANTES de liberar o app. Bloqueio limpa a sessão e mostra tela cheia.
  useEffect(() => {
    if (!user) {
      setAccess({ status: "checking" });
      return;
    }
    let cancelled = false;
    setAccess({ status: "checking" });
    checkAcesso(user)
      .then((r) => {
        if (cancelled) return;
        setVis({ somenteLeitura: r.somenteLeitura, abas: r.abas });
        if (r.allowed) {
          setAccess({ status: "allowed" });
        } else {
          // Sem acesso: anula a sessão (não deixa o app ativo).
          localStorage.removeItem(SESSION_KEY);
          encerrarSessao();
          setAccess({
            status: "blocked",
            message:
              "Você não tem acesso a este sistema. Fale com o Admin Supremo.",
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Falha de rede ao falar com o Portal: não libera (seguro), sem loop.
        setAccess({
          status: "blocked",
          message:
            "Não foi possível verificar o acesso. Tente novamente em instantes.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Resolve se o usuário logado é administrador (define quem vê/gerencia "Usuários").
  // Admin Supremo é SEMPRE admin (mesmo sem cadastro local neste sistema).
  useEffect(() => {
    setSessionUser(user ?? "");
    if (!user) {
      setIsAdmin(false);
      setPodeExcluir(false);
      setDisplayName(null);
      return;
    }
    api
      .me(user)
      .then((r) => {
        setIsAdmin(!!r.isAdmin);
        setPodeExcluir(!!r.podeExcluir);
        setDisplayName(r.nome || null);
      })
      .catch(() => {
        setIsAdmin(false);
        setPodeExcluir(false);
        setDisplayName(null);
      });
    api.permissoes
      .minhas()
      .then((p) => {
        setPodeDiagnosticar(!!p.diagnostico);
        setPodeLiberar(!!p.liberacao_cliente);
        setAbasPermitidas(p.abas ?? []);
      })
      .catch(() => {
        setPodeDiagnosticar(false);
        setPodeLiberar(false);
        // Falha ao perguntar não pode ESCONDER o sistema de quem trabalha nele:
        // sem resposta, o menu fica como sempre foi e o servidor decide.
        setAbasPermitidas([]);
      });
  }, [user]);

  // Não-admin não acessa a aba de usuários (nem por deep-link).
  useEffect(() => {
    if ((active === "usuarios" || active === "permissoes") && !isAdmin) setActive("dashboard");
  }, [active, isAdmin]);

  /*
   * Aba não liberada também não abre por deep-link (#tab=limites).
   * Sem isto, o item some do menu mas o endereço direto ainda entrava — e a
   * tela abria vazia, com o servidor recusando cada chamada por trás.
   */
  useEffect(() => {
    if (active === "dashboard" || active === "usuarios" || active === "permissoes") return;
    if (!podeVerAba(active)) setActive("dashboard");
  }, [active, abasPermitidas]);

  // Somente visualização: faixa de aviso, sem botões de gravar e sem gravação.
  useEffect(() => {
    ativarModoLeitura(access.status === "allowed" && vis.somenteLeitura);
  }, [access.status, vis.somenteLeitura]);

  /*
   * ABAS LIBERADAS PELO PORTAL (somente visualização). Valem JUNTO com a regra
   * do próprio laboratório (`podeVerAba`, da aba "Acesso e permissões"): a aba
   * só aparece quando as duas deixam. "Usuários", "Acesso e permissões" e
   * "Solicitar Atualização" não são consulta — somem para quem só visualiza.
   * Os ensaios de "Análises" e as etapas de "Controle de Seringas" e de
   * "Equipamentos de Ensaio" seguem a aba-mãe (o Portal libera a aba inteira).
   */
  const verAba = (k: string) =>
    liberadaNoPortal(vis, k) &&
    !(vis.somenteLeitura && (k === "usuarios" || k === "permissoes" || k === "solicitar_atualizacao"));
  // Aba fora das liberadas (inclusive por deep-link) cai na primeira que as
  // DUAS regras deixam — senão o efeito de cima a mandaria de volta.
  useEffect(() => {
    if (verAba(active)) return;
    const primeira = primeiraAbaLiberada(
      vis,
      ["dashboard", ...TABS.map((t) => t.key)].filter(
        (k) => verAba(k) && (k === "dashboard" || podeVerAba(k)),
      ),
    );
    if (primeira) setActive(primeira);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, vis, abasPermitidas]);

  useEffect(() => {
    if (!user || access.status !== "allowed") return;
    let vivo = true;
    api.analises
      .resumoEnsaios()
      .then((r) => {
        if (!vivo) return;
        setResumoEnsaios(r.ensaios ?? {});
        setPendentesProtocolo(r.protocolo?.pendentes ?? 0);
      })
      .catch(() => {
        if (!vivo) return;
        setResumoEnsaios({});
        setPendentesProtocolo(0);
      });
    return () => {
      vivo = false;
    };
  }, [user, access.status, refreshKey]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  const onDataChanged = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  function login(name: string) {
    localStorage.setItem(SESSION_KEY, name);
    setUser(name);
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    encerrarSessao();
    setUser(null);
    setAccess({ status: "checking" });
    setVis(ACESSO_COMPLETO);
  }

  if (!user) {
    return <Login onLogin={login} theme={theme} onToggleTheme={toggleTheme} />;
  }

  // PORTÃO DE ACESSO: enquanto valida, mostra "Verificando acesso..." (não pisca
  // o app). Se bloqueado, tela cheia com retorno ao Portal — o app NÃO é renderizado.
  if (access.status === "checking") {
    return <AccessChecking />;
  }
  if (access.status === "blocked") {
    return (
      <AccessBlocked
        message={access.message}
        onBackToPortal={() => goToSystem(SYSTEMS.portal, user)}
      />
    );
  }

  const tab = TABS.find((t) => t.key === active);
  const tipoAtivo = active === "analises" ? TIPOS_ANALISE.find((t) => t.key === analiseTipo) : undefined;
  const submenuAtivo = SUBMENUS[active];
  const subAtiva = submenuAtivo?.subabas.find((s) => s.key === subEscolhida[active]);
  const title =
    active === "dashboard"
      ? "Dashboard"
      : tipoAtivo
        ? `Análises · ${tipoAtivo.titulo}`
        : submenuAtivo && subAtiva
          ? `${submenuAtivo.titulo} · ${subAtiva.titulo}`
          : tab?.label ?? "";
  const subtitle =
    active === "dashboard"
      ? "Visão geral das análises de óleo"
      : tipoAtivo
        ? `${tipoAtivo.descricao} — OS em aberto deste ensaio e as amostras de cada uma`
        : subAtiva
          ? subAtiva.descricao
          : tab?.subtitle ?? "";

  return (
    <PermissoesProvider
      value={{
        podeExcluir,
        diagnostico: podeDiagnosticar,
        liberacaoCliente: podeLiberar,
        abas: abasPermitidas,
      }}
    >
      <AvisoSistema />
      <AvisoReuniao usuario={user} onAbrir={() => setActive("solicitar_atualizacao")} />
      <div className="app">
        <AnimatedBackground />

        <aside className="sidebar">
          <div className="brand">
            <img src={logoBranca} alt="SERCAMP" className="brand-logo-img" />
          </div>

          <nav className="nav" ref={navRef}>
            {verAba("dashboard") && (
              <button
                className={`nav-item ${active === "dashboard" ? "active" : ""}`}
                onClick={() => setActive("dashboard")}
              >
                <span className="nav-icon">
                  <IconDashboard size={19} />
                </span>
                Dashboard
              </button>
            )}
            {TABS.filter(
              (t) =>
                // "Usuários" e "Acesso e permissões" continuam sendo de
                // administrador — e NÃO entram na marcação por aba: se
                // pudessem ser tiradas, o administrador que se restringisse
                // sem querer não teria por onde desfazer.
                ((t.key !== "usuarios" && t.key !== "permissoes") || isAdmin) &&
                (t.key === "usuarios" || t.key === "permissoes" || podeVerAba(t.key)) &&
                // E as abas liberadas pelo Portal (somente visualização).
                verAba(t.key),
            ).map((t) => {
              const Icon = ICONS[t.icon];
              // "Análises" abre um mini menu com os quatro ensaios; clicar de
              // novo no item recolhe (e a aba continua onde estava).
              if (t.key === "analises") {
                return (
                  <div key={t.key} className="nav-grupo">
                    <button
                      className={`nav-item nav-item-pai ${active === t.key ? "active" : ""}`}
                      onClick={() => {
                        if (active !== t.key) {
                          setActive(t.key);
                          setAnalisesAberto(true);
                        } else {
                          setAnalisesAberto((v) => !v);
                        }
                      }}
                      aria-expanded={analisesAberto}
                    >
                      <span className="nav-icon">
                        <Icon size={19} />
                      </span>
                      {t.label}
                      <span className={`nav-chevron ${analisesAberto ? "aberto" : ""}`}>
                        <IconChevronDown size={16} />
                      </span>
                    </button>
                    {analisesAberto && (
                      <div className="nav-sub">
                        {TIPOS_ANALISE.map((tipo) => {
                          const r = resumoEnsaios[tipo.key];
                          const ativo = active === t.key && analiseTipo === tipo.key;
                          return (
                            <button
                              key={tipo.key}
                              className={`nav-subitem ${ativo ? "active" : ""}`}
                              title={
                                r
                                  ? `${tipo.descricao} — ${r.os} OS em aberto, ${r.amostras} amostra(s)` +
                                    (r.atrasadas > 0 ? `, ${r.atrasadas} fora do prazo` : "")
                                  : tipo.descricao
                              }
                              onClick={() => {
                                setActive(t.key);
                                setAnaliseTipo(tipo.key);
                              }}
                            >
                              <span className={`nav-subitem-sigla tom-${tipo.tom}`}>{tipo.sigla}</span>
                              <span className="nav-subitem-nome">{tipo.titulo}</span>
                              {/*
                                O contador só aparece quando há serviço: "0" em
                                quatro linhas é ruído que esconde o que importa.
                                Vermelho quando alguma OS passou do prazo.
                              */}
                              {r && r.os > 0 && (
                                <span className={`nav-subitem-num ${r.atrasadas > 0 ? "atraso" : ""}`}>
                                  {r.os}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              // "Controle de Seringas" e "Equipamentos de Ensaio" abrem o mesmo
              // tipo de mini menu, com as quatro etapas do caminho (SUBMENUS).
              const submenu = SUBMENUS[t.key];
              if (submenu) {
                const aberto = !!subAberto[t.key];
                return (
                  <div key={t.key} className="nav-grupo">
                    <button
                      className={`nav-item nav-item-pai ${active === t.key ? "active" : ""}`}
                      onClick={() => {
                        if (active !== t.key) {
                          setActive(t.key);
                          setSubAberto((m) => ({ ...m, [t.key]: true }));
                        } else {
                          setSubAberto((m) => ({ ...m, [t.key]: !m[t.key] }));
                        }
                      }}
                      aria-expanded={aberto}
                    >
                      <span className="nav-icon">
                        <Icon size={19} />
                      </span>
                      {t.label}
                      <span className={`nav-chevron ${aberto ? "aberto" : ""}`}>
                        <IconChevronDown size={16} />
                      </span>
                    </button>
                    {aberto && (
                      <div className="nav-sub">
                        {submenu.subabas.map((sub) => {
                          const ativo = active === t.key && subEscolhida[t.key] === sub.key;
                          return (
                            <button
                              key={sub.key}
                              className={`nav-subitem ${ativo ? "active" : ""}`}
                              title={sub.descricao}
                              onClick={() => {
                                setActive(t.key);
                                setSubEscolhida((m) => ({ ...m, [t.key]: sub.key }));
                              }}
                            >
                              <span className={`nav-subitem-sigla tom-${sub.tom}`}>{sub.sigla}</span>
                              <span className="nav-subitem-nome">{sub.titulo}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={t.key}
                  className={`nav-item ${active === t.key ? "active" : ""}`}
                  onClick={() => setActive(t.key)}
                  title={
                    t.key === "protocolo" && pendentesProtocolo > 0
                      ? `${pendentesProtocolo} OS esperando entrada no laboratório`
                      : undefined
                  }
                >
                  <span className="nav-icon">
                    <Icon size={19} />
                  </span>
                  {t.label}
                  {/* O Protocolo diz quantas OS esperam o frasco chegar — é o
                      trabalho do balcão. Zero não aparece (é ruído). */}
                  {t.key === "protocolo" && pendentesProtocolo > 0 && (
                    <span className="nav-subitem-num nav-item-num">{pendentesProtocolo}</span>
                  )}
                </button>
              );
            })}
          </nav>

        </aside>

        <main className="content">
          <header className="topbar">
            <div>
              <h1>{title}</h1>
              <p className="muted">{subtitle}</p>
            </div>

            <div className="topbar-actions">
              <button
                className="theme-toggle"
                onClick={() => goToSystem(SYSTEMS.portal, user)}
                title="Portal SERCAMP"
              >
                <IconHome size={18} />
              </button>
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                title={theme === "dark" ? "Tema claro" : "Tema escuro"}
              >
                {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </button>
              <div className="user-chip">
                <span className="user-avatar">
                  {user.charAt(0).toUpperCase()}
                </span>
                <span className="user-name">{displayName || user}</span>
              </div>
              <button className="theme-toggle" onClick={logout} title="Sair">
                <IconLogout size={18} />
              </button>
            </div>
          </header>

          <section className="view">
            {!verAba(active) ? null : active === "dashboard" ? (
              <Dashboard refreshKey={refreshKey} />
            ) : active === "protocolo" ? (
              // A porta de entrada das amostras (Controle de OS) — ver
              // components/ProtocoloView.tsx e server/protocolo.ts.
              <ProtocoloView
                key="protocolo"
                onDataChanged={onDataChanged}
                pedidoNovaOs={pedidoNovaOs}
                onPedidoAtendido={() => setPedidoNovaOs(null)}
              />
            ) : active === "analises" && tab ? (
              // A aba Análises tem navegação própria (tipo de ensaio → OS →
              // amostras da OS) — ver components/AnalisesView.tsx.
              <AnalisesView
                key={tab.key}
                tab={tab}
                onDataChanged={onDataChanged}
                tipoKey={analiseTipo}
                onTipoKeyChange={(k) => {
                  setAnaliseTipo(k);
                  if (k) setAnalisesAberto(true);
                }}
                onNovaAmostra={
                  // Só oferece o atalho a quem pode abrir o Protocolo.
                  podeVerAba("protocolo") && verAba("protocolo")
                    ? (os, cliente) => {
                        setPedidoNovaOs({ os, cliente, n: Date.now() });
                        setActive("protocolo");
                      }
                    : undefined
                }
              />
            ) : active === "seringas" ? (
              // Quatro subabas (cadastro → estoque → saída → retorno), com o
              // submenu no menu lateral — ver components/SeringasView.tsx.
              <SeringasView
                key="seringas"
                subKey={subEscolhida.seringas ?? null}
                onSubKeyChange={(k) => escolherSub("seringas", k)}
                onDataChanged={onDataChanged}
              />
            ) : active === "equipamentos_ensaio" ? (
              // Mesmas quatro subabas, mas o cadastro é o da Gestão da Operação
              // (o servidor daqui só repassa) — ver EquipamentosEnsaioView.tsx.
              <EquipamentosEnsaioView
                key="equipamentos_ensaio"
                subKey={subEscolhida.equipamentos_ensaio ?? null}
                onSubKeyChange={(k) => escolherSub("equipamentos_ensaio", k)}
                onDataChanged={onDataChanged}
              />
            ) : active === "permissoes" ? (
              <PermissoesView />
            ) : active === "historico" ? (
              <Historico />
            ) : active === "aprovacao" ? (
              <AprovacaoView />
            ) : tab?.key === "solicitar_atualizacao" ? (
              // A aba que existe em todos os sistemas: o pedido vai para o
              // sistema de Desenvolvimento (ver server/solicitar-atualizacao.ts).
              <SolicitarAtualizacaoView usuario={user} />
            ) : tab ? (
              <CrudView key={tab.key} tab={tab} onDataChanged={onDataChanged} />
            ) : null}
          </section>
        </main>
      </div>
    </PermissoesProvider>
  );
}
